/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useMemo } from 'react'
import axios from 'axios'
import { API_URL } from '../../../modules/auth/core/_requests'
import { useAuth } from '../../../modules/auth'
import AIOptions, { AIOptionValues, defaultAIOptionValues } from './components/AIOptions'

// PrimeReact imports (assumes PrimeReact is installed & configured)
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'

interface AutoBloggerResponse {
  blogContent: string
}

interface ShopifyArticle {
  id: number
  title: string
  created_at: string
  updated_at: string
}

export const AutoBloggerPage: FC = () => {
  const { currentUser } = useAuth()
  const currentStoreName = currentUser?.decoded?.current_shopify_store || ''

  // Form fields
  const [blogTitle, setBlogTitle] = useState('')
  const [keywords, setKeywords] = useState('')
  const [aiOptions, setAiOptions] = useState<AIOptionValues>(defaultAIOptionValues)

  // New state for ideas keywords input
  const [ideasKeywords, setIdeasKeywords] = useState('')

  // Generated content
  const [generatedBlogContent, setGeneratedBlogContent] = useState('') // raw from AI
  // Editable content (user can tweak before push)
  const [editedBlogContent, setEditedBlogContent] = useState('')

  // Loading / status
  const [loading, setLoading] = useState(false)

  // Toggle for viewing existing Shopify blogs
  const [showExistingBlogs, setShowExistingBlogs] = useState(false)
  const [shopifyBlogs, setShopifyBlogs] = useState<ShopifyArticle[]>([])
  const [blogsLoading, setBlogsLoading] = useState(false)

  // -------- NEW STATES FOR "GET IDEAS" FEATURE --------
  const [showIdeas, setShowIdeas] = useState(false)
  const [ideasLoading, setIdeasLoading] = useState(false)
  const [ideasError, setIdeasError] = useState('')
  const [ideasList, setIdeasList] = useState<string[]>([])

  // NEW: State to store details of inserted internal links
  const [insertedLinks, setInsertedLinks] = useState<{ keyword: string, url: string }[]>([])

  // Generate blog post
  const handleGenerateBlog = async () => {
    if (!blogTitle.trim()) {
      alert('Please provide a blog title.')
      return
    }
    setLoading(true)
    setGeneratedBlogContent('Generating blog post... Please wait.\n')
    setEditedBlogContent('')
    try {
      const resp = await axios.post<AutoBloggerResponse>(`${API_URL}/api/auto_blogger/generate`, {
        storeName: currentStoreName,
        blogTitle: blogTitle.trim(),
        keywords: keywords.trim() || 'none',
        ...aiOptions,
      })
      setGeneratedBlogContent(resp.data.blogContent)
      setEditedBlogContent(resp.data.blogContent)
      // Clear any previously inserted links
      setInsertedLinks([])
    } catch (err: any) {
      console.error(err)
      alert('Failed to generate blog content.')
      setGeneratedBlogContent('')
      setEditedBlogContent('')
    } finally {
      setLoading(false)
    }
  }

  // Push to Shopify
  const handlePushToShopify = async () => {
    if (!editedBlogContent.trim()) {
      alert('Please generate blog content first (and optionally edit).')
      return
    }
    if (!blogTitle.trim()) {
      alert('Please provide a blog title.')
      return
    }
    setLoading(true)
    try {
      await axios.post(`${API_URL}/api/auto_blogger/push`, {
        storeName: currentStoreName,
        blogTitle: blogTitle.trim(),
        blogContent: editedBlogContent.trim(),
      })
      alert('Blog article pushed successfully!')
      setBlogTitle('')
      setKeywords('')
      setGeneratedBlogContent('')
      setEditedBlogContent('')
      setInsertedLinks([])
    } catch (err: any) {
      console.error(err)
      alert('Failed to push article to Shopify.')
    } finally {
      setLoading(false)
    }
  }

  // NEW: Add Internal Linking – calls our new API endpoint
  const handleAddInnerlinking = async () => {
    if (!editedBlogContent.trim()) {
      alert('No blog content to process for internal linking.')
      return
    }
    setLoading(true)
    try {
      const resp = await axios.post(`${API_URL}/api/auto_innerlink/insert`, {
        storeName: currentStoreName,
        blogContent: editedBlogContent.trim(),
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
      })
      // Expect response: { modifiedHtml, insertedLinks }
      setEditedBlogContent(resp.data.modifiedHtml)
      setInsertedLinks(resp.data.insertedLinks || [])
      alert('Internal linking added successfully!')
    } catch (err: any) {
      console.error(err)
      alert('Failed to add internal linking.')
    } finally {
      setLoading(false)
    }
  }

  // Fetch existing blogs from Shopify
  const fetchShopifyBlogs = async () => {
    setBlogsLoading(true)
    try {
      const resp = await axios.get<{ articles: ShopifyArticle[] }>(
        `${API_URL}/api/auto_blogger/blogs?storeName=${currentStoreName}`
      )
      setShopifyBlogs(resp.data.articles || [])
    } catch (error: any) {
      console.error(error)
      alert('Failed to load existing Shopify blogs.')
    } finally {
      setBlogsLoading(false)
    }
  }

  // Toggle existing blogs panel
  const handleToggleBlogs = () => {
    const newState = !showExistingBlogs
    setShowExistingBlogs(newState)
    if (newState && shopifyBlogs.length === 0) {
      fetchShopifyBlogs()
    }
  }

  // ------------- NEW FUNCTIONS FOR "GET IDEAS" -------------
  const handleToggleIdeas = () => {
    const newState = !showIdeas
    setShowIdeas(newState)
    // If user opens "Ideas" and none are loaded yet, fetch them
    if (newState && ideasList.length === 0) {
      fetchIdeas()
    }
  }

  const fetchIdeas = async () => {
    setIdeasError('')
    setIdeasList([])
    setIdeasLoading(true)
    try {
      const resp = await axios.post<{ ideas: string }>(`${API_URL}/api/auto_blogger/ideas`, {
        storeName: currentStoreName,
        keywords: ideasKeywords.trim(),
        aiModel: aiOptions.aiModel,
        aiEffort: aiOptions.aiEffort,
      })
      const rawIdeas = resp.data.ideas
      const lines = rawIdeas.split('\n').map(line => line.trim()).filter(line => line)
      const cleanedLines = lines.map(line => {
        let newLine = line.replace(/\*/g, '')
        newLine = newLine.replace(/^\d+[\.\)]\s*/, '')
        return newLine.trim()
      })
      setIdeasList(cleanedLines)
    } catch (error: any) {
      console.error(error)
      setIdeasError('Failed to load ideas. Please try again.')
    } finally {
      setIdeasLoading(false)
    }
  }

  const handleSelectIdea = (idea: string) => {
    setBlogTitle(idea)
    setShowIdeas(false)
  }

  // Add reset function
  const handleReset = () => {
    setBlogTitle('')
    setKeywords('')
    setIdeasKeywords('')
    setGeneratedBlogContent('')
    setEditedBlogContent('')
    setInsertedLinks([])
    setShowIdeas(false)
    setShowExistingBlogs(false)
  }

  // ---------- NEW: Helper Functions for Blog Stats ----------
  const countSyllables = (word: string): number => {
    word = word.toLowerCase()
    if (word.length <= 3) return 1
    word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '')
    word = word.replace(/^y/, '')
    const sylMatches = word.match(/[aeiouy]{1,2}/g)
    return sylMatches ? sylMatches.length : 0
  }

  const stats = useMemo(() => {
    // Remove HTML tags to work with plain text
    const strippedText = editedBlogContent.replace(/<[^>]+>/g, ' ').trim()
    const characters = strippedText.length
    const letters = (strippedText.match(/[a-zA-Z]/g) || []).length
    const wordsArray = strippedText.split(/\s+/).filter(Boolean)
    const words = wordsArray.length
    const sentencesArray = strippedText.split(/[.!?]+/).filter((s) => s.trim().length > 0)
    const sentences = sentencesArray.length
    const paragraphsArray = strippedText.split(/\n+/).filter((s) => s.trim().length > 0)
    const paragraphs = paragraphsArray.length
    let syllableCount = 0
    wordsArray.forEach((word) => {
      syllableCount += countSyllables(word)
    })
    const readingScore =
      sentences > 0 && words > 0
        ? 0.39 * (words / sentences) + 11.8 * (syllableCount / words) - 15.59
        : 0
    const totalReadingSeconds = (words / 200) * 60
    const minutes = Math.floor(totalReadingSeconds / 60)
    const seconds = Math.round(totalReadingSeconds % 60)
    return {
      readingScore: readingScore.toFixed(1),
      letters,
      characters,
      words,
      sentences,
      paragraphs,
      readingTime: `${minutes}m ${seconds}s`,
    }
  }, [editedBlogContent])
  // ---------------------------------------------------------

  return (
    <>
      <h1 className='mt-3 ms-3'>Auto Blogger - SEO Content Writer</h1>
      <div className='card mx-3 mt-4'>
        <div className='card-body'>
          {/* Title input */}
          <div className='mb-3'>
            <label className='form-label'>
              Blog Title <span className='text-danger'>*</span>
            </label>
            <input
              type='text'
              className='form-control'
              value={blogTitle}
              onChange={(e) => setBlogTitle(e.target.value)}
              placeholder='Enter blog title...'
            />
          </div>

          {/* Keywords input */}
          <div className='mb-3'>
            <label className='form-label'>Keywords (optional, comma-separated)</label>
            <input
              type='text'
              className='form-control'
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder='e.g. vape, e-cigarette, coils...'
            />
          </div>

          {/* AI Options */}
          <AIOptions value={aiOptions} onChange={setAiOptions} />

          {/* Buttons and Ideas Keywords input */}
          <div className='mb-3 d-flex flex-wrap gap-2'>
            {/* Primary Actions Group */}
            <div className='me-auto'>
              <button
                className='btn btn-success me-2'
                onClick={handleGenerateBlog}
                disabled={loading}
              >
                Generate Blog Post
              </button>
              <button
                className='btn btn-primary me-2'
                onClick={handlePushToShopify}
                disabled={loading || !editedBlogContent.trim()}
              >
                Push to Shopify
              </button>
              <button
                className='btn btn-warning me-2'
                onClick={handleAddInnerlinking}
                disabled={loading || !editedBlogContent.trim()}
              >
                Add Innerlinking
              </button>
            </div>

            {/* Secondary Actions Group */}
            <div className='d-flex align-items-center gap-2'>
              <button 
                className='btn btn-outline-secondary'
                onClick={handleToggleBlogs}
              >
                {showExistingBlogs ? 'Hide Existing Blogs' : 'Show Existing Blogs'}
              </button>
              <button 
                className='btn btn-outline-info'
                onClick={handleToggleIdeas}
              >
                {showIdeas ? 'Hide Ideas' : 'Get Ideas'}
              </button>
              <input
                type='text'
                className='form-control'
                style={{ width: '200px' }}
                placeholder='Idea Keywords (optional)'
                value={ideasKeywords}
                onChange={(e) => setIdeasKeywords(e.target.value)}
              />
              <button
                className='btn btn-link text-white'
                onClick={handleReset}
                disabled={loading}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CARD: Inserted Internal Links */}
      {insertedLinks.length > 0 && (
        <div className='card mx-3 mt-2'>
          <div className='card-header'>Inserted Internal Links</div>
          <div className='card-body'>
            <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
              {insertedLinks.map((link, idx) => (
                <li key={idx}>
                  <strong>{link.keyword}</strong> linked to <code>{link.url}</code>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* EXISTING BLOGS PANEL */}
      {showExistingBlogs && (
        <div className='card mx-3 mt-2'>
          <div className='card-header'>Your Shopify Blogs</div>
          <div className='card-body'>
            {blogsLoading ? (
              <div>Loading existing blogs...</div>
            ) : (
              <DataTable value={shopifyBlogs} responsiveLayout='scroll'>
                <Column field='title' header='Title' />
                <Column field='created_at' header='Date Created' />
                <Column field='updated_at' header='Date Updated' />
              </DataTable>
            )}
          </div>
        </div>
      )}

      {/* IDEAS PANEL */}
      {showIdeas && (
        <div className='card mx-3 mt-2'>
          <div className='card-header'>20 New Blog Ideas</div>
          <div className='card-body'>
            {ideasLoading && <div>Loading ideas...</div>}
            {!ideasLoading && ideasError && <div className='text-danger'>Error: {ideasError}</div>}
            {!ideasLoading && !ideasError && (
              <ul
                style={{
                  listStyle: 'none',
                  paddingLeft: 0,
                  margin: 0,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                }}
              >
                {ideasList.map((idea, idx) => (
                  <li
                    key={idx}
                    onClick={() => setBlogTitle(idea)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: '#1a2b4c',
                      borderRadius: '12px',
                      margin: '4px 0',
                      padding: '6px 6px',
                      fontSize: '16px',
                      fontWeight: 500,
                      transition: 'background-color 0.2s ease-in-out',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      color: '#d1d9e6',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#243a5e'
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = '#1a2b4c'
                    }}
                  >
                    {idea}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* EDITOR + PREVIEW */}
      <div className='row mx-3 mt-3'>
        <div className='col-md-4 mb-3'>
          <div className='card'>
            <div className='card-header'>Editable HTML</div>
            <div className='card-body' style={{minHeight: '300px'}}>
              {loading ? (
                <div className='text-muted'>Loading...</div>
              ) : (
                <textarea
                  className='form-control'
                  style={{height: '300px'}}
                  value={editedBlogContent}
                  onChange={(e) => setEditedBlogContent(e.target.value)}
                />
              )}
            </div>
          </div>
          <div className='card mt-3'>
            <div className='card-header'>Blog Stats</div>
            <div className='card-body'>
              <p>Reading Score: {stats.readingScore}</p>
              <p>Letters: {stats.letters}</p>
              <p>Characters: {stats.characters}</p>
              <p>Words: {stats.words}</p>
              <p>Sentences: {stats.sentences}</p>
              <p>Paragraphs: {stats.paragraphs}</p>
              <p>Reading Time: {stats.readingTime}</p>
            </div>
          </div>
        </div>
        <div className='col-md-8 mb-3'>
          <div className='card'>
            <div className='card-header'>Rendered Preview</div>
            <div className='card-body' style={{minHeight: '300px', overflowY: 'auto'}}>
              {loading ? (
                <div className='text-muted'>Loading...</div>
              ) : editedBlogContent ? (
                <div dangerouslySetInnerHTML={{__html: editedBlogContent}} />
              ) : (
                <p className='text-muted'>No blog content generated yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export const AutoBloggerWrapper: FC = () => {
  return <AutoBloggerPage />
}
